import { computed, inject, Injectable, Signal } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { BreakpointObserver } from '@angular/cdk/layout';
import { ResponsiveService } from './responsive.service';

@Injectable({
	providedIn: 'root',
})
export class LayoutService {
	private readonly responsive = inject(ResponsiveService);
	private readonly breakpointObserver = inject(BreakpointObserver);

	private _sidenavOpenSubject = new BehaviorSubject<boolean>(false);
	sidenavOpen$ = this._sidenavOpenSubject.asObservable();

	private _sidenavCollapsedSubject = new BehaviorSubject<boolean>(false);
	sidenavCollapsed$ = this._sidenavCollapsedSubject.asObservable();

	private _sidenavCollapsedOpenSubject = new BehaviorSubject<boolean>(false);
	sidenavCollapsedOpen$ = this._sidenavCollapsedOpenSubject.asObservable();

	// ── Responsive-Erkennung: delegiert an die zentrale ResponsiveService (SSOT) ─────────────
	// Frueher hier verstreut + widerspruechlich (isMobile() ≤480 vs isMobile$ ≤599). isMobile/
	// isLtLg sind jetzt Signals — als x() weiterhin aufrufbar, daher bleiben alle bestehenden
	// Aufrufstellen gueltig und werden nebenbei reaktiv.
	readonly isDesktop$: Observable<boolean> = this.responsive.isDesktop$;
	readonly ltLg$: Observable<boolean> = this.responsive.ltLg$;

	/** Reine Mobilansicht (< 600px) — VEREINHEITLICHT (war ≤480; jetzt EINE Wahrheit). */
	readonly isMobile: Signal<boolean> = this.responsive.isMobile;
	/** Alles unterhalb Desktop (≤1279) — entspricht der frueheren isLtLg-Schwelle. */
	readonly isLtLg: Signal<boolean> = this.responsive.ltDesktop;

	// Feinere Geraete-Schwellen (Tablet hoch / quer), die das 3-Klassen-Modell der ResponsiveService
	// BEWUSST NICHT abbildet — kein „Legacy"/TODO, sondern eine eigene Achse: uvi-Charts (Groessen-
	// Kaskade), Breadcrumb/Ticket/ara-context-detail-cards (Kompakt- bzw. Swiper-Umschaltung ≤767) und
	// Calendar (≤1024). Aufs zentrale isTablet (600–1279) umzustellen wuerde diese Layouts bis 1279
	// dehnen (z.B. Breadcrumb-Kompakt, dokumentiert unerwuenscht) → daher absichtlich eigenstaendig.
	isTablet = (): boolean => this.breakpointObserver.isMatched('(min-width: 481px) and (max-width: 767px)');
	isTabletLandscape = (): boolean => this.breakpointObserver.isMatched('(min-width: 768px) and (max-width: 1024px)');

	// Tabellen-Spaltendichte: kompakte Spalten, solange der Inhalt (abzgl. SideNav-Breite) zu
	// schmal ist. Schwelle haengt vom SideNav-Zustand ab — eingeklappt = mehr Platz = kompakt
	// erst < 1300px, ausgeklappt = weniger Platz = kompakt schon < 1700px.
	// (Frueherer Bug: der Ausdruck pruefte `sidenavCollapsed$` als OBJEKT — immer truthy — statt
	//  dessen Wert; der 1300er-Zweig war tot, es galt effektiv immer ≤1700 unabh. vom Zustand.)
	isLtMdWithSideNav = (): boolean =>
		this._sidenavCollapsedSubject.value
			? this.breakpointObserver.isMatched('(max-width: 1300px)')
			: this.breakpointObserver.isMatched('(max-width: 1700px)');

	/**
	 * Burger-Sichtbarkeit: unterhalb Desktop. Die portal-spezifische „integrated"-Theme-Unterscheidung
	 * (HK4) entfällt im customerportal — hier gilt einheitlich die Desktop-Schwelle.
	 */
	readonly burgerVisible: Signal<boolean> = computed((): boolean => this.responsive.ltDesktop());

	openSidenav() {
		this._sidenavOpenSubject.next(true);
	}

	closeSidenav() {
		this._sidenavOpenSubject.next(false);
	}

	collapseSidenav() {
		this._sidenavCollapsedSubject.next(true);
	}

	expandSidenav() {
		this._sidenavCollapsedSubject.next(false);
	}

	collapseOpenSidenav() {
		this._sidenavCollapsedOpenSubject.next(true);
	}

	collapseCloseSidenav() {
		this._sidenavCollapsedOpenSubject.next(false);
	}
}
